// emailApi — client wrapper for the dispatch Email Inbox. Talks to the
// Netlify functions read-emails / send-email, which reach the mailbox over
// IMAP/SMTP server-side (credentials live in site env vars, never in the
// browser). Requires `netlify dev` (port 8888) locally so the functions are
// served — plain `vite` (port 3000) has no functions.

import { auth } from './firebase';
import { USE_SUPABASE, supabase } from './supabaseClient';
import { authedFetch } from './forwardedSmsApi';

export type EmailFolder = 'inbox' | 'sent';

export interface EmailAttachment {
  name: string;
  type: string;
  size: number;
  path?: string;
  cid?: string | null;
  inline?: boolean;
  error?: string;
  /** Stored cut at the IMAP source cap (2026-10-09): true = the server's retry
   *  may still replace it whole; 'final' = it never can. Absent = whole. */
  partial?: true | 'final';
  /** Attempts at the whole copy so far (the server stops at 3). */
  tries?: number;
  /** Set when the stored photo is the server's compressed copy (2026-10-09):
   *  the size as received; `size` is then the stored, compressed size. */
  originalSize?: number;
}

export interface EmailRecord {
  uid: number;
  messageId: string;
  folder: EmailFolder;
  from: string;
  fromName: string;
  to: string;
  subject: string;
  date: number; // ms epoch
  snippet: string;
  bodyText: string;
  bodyHtml: string;
  read: boolean;
  // Gmail-style star — shared across consoles like `read`. Absent = not starred.
  starred?: boolean;
  // Inbound attachments (any type). `path` is the object in the private
  // 'email-media' bucket; absent when it could not be stored (`error` says why).
  attachments?: EmailAttachment[];
  // Reply headers (captured by read-emails) — power conversation threading.
  inReplyTo?: string;
  references?: string[];
  // Claim queue — set on the thread's inbox messages (see emailCache).
  assignedTo?: string | null;
  assignedToName?: string | null;
  claimedAt?: number | null;
}

const READ_FN = '/.netlify/functions/read-emails';
const SEND_FN = '/.netlify/functions/send-email';

// Fetch straight from the mailbox over IMAP. With sinceUid, only messages
// newer than that UID come back (incremental sync — see services/emailCache).
export async function fetchEmails(limit = 25, folder: EmailFolder = 'inbox', sinceUid = 0): Promise<EmailRecord[]> {
  const params = new URLSearchParams({ limit: String(limit), folder });
  if (sinceUid > 0) params.set('sinceUid', String(sinceUid));
  // read-emails requires a signed-in dispatch user (2026-09-11) — same bearer the send path sends.
  const res = await fetch(`${READ_FN}?${params.toString()}`, { headers: { ...(await authHeader()) } });
  if (!res.ok) throw new Error(await errMsg(res, 'Could not load emails'));
  const data = await res.json();
  return ((data.emails || []) as EmailRecord[]).map((e) => ({ ...e, folder }));
}

export interface OutgoingAttachment {
  filename: string;
  contentType: string; // image/* only — the function rejects anything else
  contentBase64: string;
}

// Send a fresh email (compose). Used by the dispatch inbox to email the
// CUSTOMER directly — the inbox shows AI Receptionist notifications, but replies
// go to the customer, not back to the AI, so the dispatcher types the recipient.
export async function sendEmail(input: {
  to?: string;
  toSelf?: boolean; // send to the connected mailbox itself (address stays server-side)
  subject: string;
  text: string;
  inReplyTo?: string;
  references?: string; // space-separated Message-IDs, oldest first
  attachments?: OutgoingAttachment[];
}): Promise<{ success: boolean; messageId: string }> {
  const res = await fetch(SEND_FN, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await errMsg(res, 'Could not send email'));
  return res.json();
}

// send-email/delete-email verify this token server-side and require a
// dispatch-level role — the mailbox is not an open relay. _email-auth routes on
// the token's issuer, so either a Firebase ID token or a Supabase access token
// is accepted; sending the wrong one (or none) surfaces as "Sign in required".
async function authHeader(): Promise<Record<string, string>> {
  try {
    if (USE_SUPABASE) {
      const { data } = await supabase().auth.getSession();
      const token = data.session?.access_token;
      return token ? { Authorization: `Bearer ${token}` } : {};
    }
    const token = await auth?.currentUser?.getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

async function errMsg(res: Response, fallback: string): Promise<string> {
  try {
    const err = await res.json();
    if (err && err.error) return err.error;
  } catch {
    if (res.status === 404) {
      return 'Email API not found — run `netlify dev` (port 8888) so functions are served.';
    }
  }
  return `${fallback} (${res.status})`;
}

// The composer's AI wand (Umer, 2026-09-16; command mode 2026-09-17). Two
// things can be in the box and the server decides which:
//   • 'polish'  — a rough draft comes back as a proper customer email —
//                 greeting by name, grammar fixed, house style, sign-off;
//   • 'command' — a short instruction ("confirmation mail bhejo", "isko $150
//                 pay karne ko bolo") comes back as the COMPLETE email, subject
//                 included, built from the house template that matches and
//                 this order's facts (`order` + the pre-rendered `candidates`).
// Null when the service is unavailable so the caller can leave the text
// untouched and say so; never throws into the composer.
export type EmailAiMode = 'polish' | 'command';
export interface EmailAiResult {
  mode: EmailAiMode;
  body: string;
  subject: string | null; // only in command mode
  template: string | null; // the house template key used, when one was
}
export interface EmailAiCandidate { key: string; label: string; subject: string; body: string }
export interface EmailAiOrder {
  orderNumber?: string;
  customerName?: string;
  customerEmail?: string;
  phone?: string;
  address?: string;
  city?: string;
  total?: number;
  itemsSummary?: string;
  paymentMethod?: string;
  orderType?: string;
  trackingNumber?: string;
}
export async function polishEmailDraft(payload: {
  draft: string;
  customerName?: string;
  subject?: string;
  orderNumber?: string;
  lastInbound?: string;
  context?: string[];
  order?: EmailAiOrder;
  candidates?: EmailAiCandidate[];
  compose?: boolean;
}): Promise<EmailAiResult | null> {
  try {
    const res = await authedFetch('/.netlify/functions/email-polish', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return null;
    const data = await res.json();
    const body = typeof data.polished === 'string' ? data.polished.trim() : '';
    if (!body) return null;
    const mode: EmailAiMode = data.mode === 'command' ? 'command' : 'polish';
    return {
      mode,
      body,
      subject: mode === 'command' && typeof data.subject === 'string' && data.subject.trim() ? data.subject.trim() : null,
      template: mode === 'command' && typeof data.template === 'string' && data.template ? data.template : null,
    };
  } catch {
    return null;
  }
}
